import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import CareIllustration from "../components/CareIllustration";
import TransplantMilestone from '../components/TransplantMilestone';
import ServiceExplorer from "../components/ServiceExplorer";
import "../home.css";

export default function Home() {
  const [doctors, setDoctors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [query, setQuery] = useState("");
  const [profile, setProfile] = useState(null);
  const dialog = useRef(null);
  const home = useRef(null);
  useEffect(() => {
    if (!window.IntersectionObserver) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(({ target, isIntersecting }) => {
          target.classList.toggle("is-inview", isIntersecting);
          if (isIntersecting) target.classList.add("is-revealed");
        });
      },
      { threshold: 0.12 },
    );
    home.current
      .querySelectorAll(
        ".nc-hero-scene, .nc-section-heading, .nc-service-tile, .nc-doctor-card, .nc-steps li, .nc-app-copy, .nc-phone, .nc-faq>div",
      )
      .forEach((element) => {
        element.classList.add("nc-motion-target");
        observer.observe(element);
      });
    return () => observer.disconnect();
  }, [doctors.length]);
  const loadDoctors = async (signal) => {
    setLoading(true);
    setError(false);
    try {
      const response = await fetch(
        "https://dashboard.nephrocorenexus.com/api/users/doctors",
        { signal, cache: "no-store" },
      );
      if (!response.ok) throw new Error("Directory unavailable");
      const payload = await response.json();
      if (!payload.success || !Array.isArray(payload.data))
        throw new Error("Directory unavailable");
      setDoctors(
        payload.data
          .filter(
            (doctor) =>
              doctor.name &&
              doctor.qualifications &&
              doctor.specialties?.length,
          )

      );
    } catch (err) {
      if (err.name !== "AbortError") setError(true);
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  };
  useEffect(() => {
    const controller = new AbortController();
    loadDoctors(controller.signal);
    const refresh = () => { if (!document.hidden) loadDoctors(controller.signal); };
    const timer = setInterval(refresh, 30000);
    window.addEventListener('focus', refresh);
    return () => { controller.abort(); clearInterval(timer); window.removeEventListener('focus', refresh); };
  }, []);
  useEffect(() => {
    document.title = "NephroCore Nexus | Kidney care, connected";
  }, []);
  const openDetails = (profile) => {
    setProfile(profile);
    dialog.current.showModal();
  };
  const shown = doctors
    .filter((doctor) =>
      `${doctor.name} ${doctor.specialties.join(" ")} ${doctor.hospitalName || ""}`
        .toLowerCase()
        .includes(query.toLowerCase()),
    )
    .slice(0, 6);
  return (
    <main
      id="main-content"
      className="nc-home"
      ref={home}
    >
      <section className="nc-hero">
        <div className="container nc-hero-grid">
          <div className="nc-hero-copy">
            <span className="nc-eyebrow">
              <i /> YOUR HEALTH. OUR CONNECTION.
            </span>
            <h1>
              A little more care.
              <br />
              <em>A lot more connected.</em>
            </h1>
            <p>
              Find a doctor. Book a lab test. Keep your health records close. A
              simpler way to navigate kidney care, all in one place.
            </p>
            <div className="nc-hero-buttons">
              <a className="nc-button" href="#care">
                Find your care <span aria-hidden="true">→</span>
              </a>
              <a className="nc-text-link" href="#how-it-works">
                See how it works <span aria-hidden="true">→</span>
              </a>
            </div>
            <div className="nc-hero-note">
              <span className="nc-check">✓</span>
              <span>
                For your first consultation.
                <br />
                <strong>And every step after.</strong>
              </span>
            </div>
          </div>
          <div className="nc-hero-scene">
            <div className="nc-scene-label">
              <span>CARE THAT CONNECTS</span>
              <b>You + your care team</b>
            </div>
            <CareIllustration kind="team" className="nc-doctor-illustration" />
            <div className="nc-float-card">
              <span className="nc-mini-icon">♡</span>
              <div>
                <strong>Your health, together.</strong>
                <small>Doctors · Diagnostics · Records</small>
              </div>
            </div>

          </div>
        </div>
      </section>
      <div className="nc-service-strip">
        <div className="container">
          <span>Specialist consultations</span>
          <i />
          <span>Laboratory bookings</span>
          <i />
          <span>Digital health records</span>
          <i />
          <span>Care memberships</span>
        </div>
      </div>
      <ServiceExplorer />
      <section id="doctors" className="nc-doctors-section">
        <div className="container">
          <div className="nc-section-heading">
            <div>
              <span className="nc-eyebrow">PEOPLE BEHIND YOUR CARE</span>
              <h2>Find a doctor you can connect with.</h2>
            </div>
            <p>
              Explore profiles from our care directory.
              <br />
              Choose your next step with confidence.
            </p>
          </div>
          <form
            className="nc-doctor-search"
            onSubmit={(event) => event.preventDefault()}
          >
            <label htmlFor="doctor-search">Search our doctors</label>
            <div>
              <span aria-hidden="true">⌕</span>
              <input
                id="doctor-search"
                type="search"
                placeholder="Doctor name, specialty or hospital"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
              />
              {query && (
                <button type="button" onClick={() => setQuery("")}>
                  Clear
                </button>
              )}
            </div>
          </form>
          {loading ? (
            <p role="status" className="nc-empty">
              Loading the doctor directory…
            </p>
          ) : error ? (
            <div role="alert" className="nc-empty">
              <p>We couldn’t load the directory right now.</p>
              <button className="nc-button small" onClick={() => loadDoctors()}>
                Try again
              </button>
            </div>
          ) : !shown.length ? (
            <p role="status" className="nc-empty">
              {query
                ? "No matching profiles. Try another name or specialty."
                : "Doctor profiles are currently unavailable. Contact our team for help finding care."}
            </p>
          ) : (
            <div className="nc-doctor-grid">
              {shown.map((doctor) => (
                <article className="nc-doctor-card" key={doctor._id}>
                  <div className="nc-doctor-photo">
                    <CareIllustration kind="doctor" />
                  </div>
                  <div className="nc-doctor-info">
                    <p className="nc-doctor-specialty">
                      {doctor.specialties.join(" · ")}
                    </p>
                    <h3>{doctor.name}</h3>
                    <p>{doctor.qualifications}</p>
                    {doctor.experience > 0 && (
                      <p>{doctor.experience} years of experience</p>
                    )}
                    <div className="nc-doctor-bottom">
                      <span>
                        {Number.isFinite(doctor.consultationFee)
                          ? `₹${doctor.consultationFee.toLocaleString("en-IN")}`
                          : "View profile"}
                        <small>
                          {Number.isFinite(doctor.consultationFee)
                            ? "consultation fee"
                            : "for care details"}
                        </small>
                      </span>
                      <button
                        onClick={() => openDetails(doctor, true)}
                        aria-label={`View profile for ${doctor.name}`}
                      >
                        View profile <span aria-hidden="true">→</span>
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
          <p className="nc-directory-note">
            Profiles and fees are provided by the care directory. Sign in to the
            patient workspace to review availability and book online.
          </p>
        </div>
      </section>
      <section
        id="how-it-works"
        className="nc-section container nc-steps-section"
      >
        <div>
          <span className="nc-eyebrow">LESS RUNNING AROUND. MORE CLARITY.</span>
          <h2>
            Your care journey,
            <br />
            made a little simpler.
          </h2>
          <p>One place to begin, and a familiar place to return to.</p>
          <a className="nc-text-link" href="#get-app">
            Meet your health companion →
          </a>
        </div>
        <ol className="nc-steps">
          {[
            [
              "Make it yours",
              "Create your profile and add the health information you want your care team to know.",
            ],
            [
              "Choose your next step",
              "Explore doctors, tests and membership options. Review the details before you book.",
            ],
            [
              "Keep it together",
              "Return to your appointment history and available reports whenever you need them.",
            ],
          ].map(([title, text], i) => (
            <li key={title}>
              <span>0{i + 1}</span>
              <div>
                <h3>{title}</h3>
                <p>{text}</p>
              </div>
            </li>
          ))}
        </ol>
      </section>
      <TransplantMilestone />
      <section id="get-app" className="container nc-app-section">
        <div className="nc-app-copy">
          <span className="nc-eyebrow">A COMPANION FOR YOUR CARE</span>
          <h2>
            Your health doesn’t
            <br />
            have to feel scattered.
          </h2>
          <p>
            Bring your appointments, lab bookings and health records together
            with NephroCore Nexus.
          </p>
          <Link className="nc-button light" to="/patient">
            Open your patient workspace →
          </Link>
          <p className="nc-app-note">
            Use your existing patient account. Your app and website share the
            same care records.
          </p>
        </div>
        <div className="nc-phone" aria-label="Illustration of the patient app">
          <div className="nc-phone-bar" />
          <div className="nc-phone-brand">
            <img src="/nephrocore-depth.svg" alt="" />
            nephrocore
          </div>
          <small>YOUR HEALTH, CONNECTED</small>
          <h3>
            A little care,
            <br />
            every day.
          </h3>
          <div className="nc-phone-tiles">
            <div>
              <CareIllustration kind="consult" />
              <span>Doctors</span>
            </div>
            <div>
              <CareIllustration kind="labwork" />
              <span>Lab tests</span>
            </div>
          </div>
          <div className="nc-phone-record">
            <CareIllustration kind="records" />
            <div>
              <strong>Your health records</strong>
              <p>Ready when you need them.</p>
            </div>
          </div>
          <span className="nc-phone-caption">App illustration</span>
        </div>
      </section>
      <section className="nc-section container nc-faq">
        <div>
          <span className="nc-eyebrow">A FEW THINGS YOU MIGHT WONDER</span>
          <h2>Let’s make it clear.</h2>
          <p>
            Need a hand? <Link to="/contact">Talk to our team →</Link>
          </p>
        </div>
        <div>
          {[
            [
              "Where do I book a consultation?",
              "Sign in to the patient workspace on this website or use the NephroCore Nexus patient app. Explore doctors, choose a consultation time and review the final price before booking.",
            ],
            [
              "Can I browse laboratory tests?",
              "Yes. The patient workspace and app list available tests and laboratory partners. Pricing and available services can vary, so review the details before booking.",
            ],
            [
              "Where can I see membership benefits?",
              "Current plans and their included benefits are shown in your patient workspace and app. Review the price, duration and conditions of the plan you choose.",
            ],
          ].map(([q, a]) => (
            <details key={q}>
              <summary>
                {q}
                <span aria-hidden="true">+</span>
              </summary>
              <p>{a}</p>
            </details>
          ))}
        </div>
      </section>
      <dialog
        aria-labelledby="details-title"
        ref={dialog}
        className="nc-dialog"
        onClick={(event) => {
          if (event.target === dialog.current) dialog.current.close();
        }}
      >
        <button
          className="nc-dialog-close"
          onClick={() => dialog.current.close()}
          aria-label="Close details"
        >
          ×
        </button>
        {profile && (
          <>
            <span className="nc-eyebrow">DOCTOR PROFILE</span>
            <h2 id="details-title">{profile.name}</h2>
            <p>{profile.qualifications}</p>
            <p>{profile.specialties.join(" · ")}</p>
            {profile.hospitalName && <p>{profile.hospitalName}</p>}
            <p>
              Review consultation times and book securely in your patient
              workspace.
            </p>
          </>
        )}
        <Link
          className="nc-button"
          to={profile ? `/patient/doctors/${profile._id}` : "/patient/doctors"}
          onClick={() => dialog.current.close()}
        >
          View times & book →
        </Link>
        <button
          className="nc-dialog-back"
          onClick={() => dialog.current.close()}
        >
          Keep exploring
        </button>
      </dialog>
    </main>
  );
}
